import { Component, ElementRef, forwardRef, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-domain-list-control',
  templateUrl: './domain-list-control.component.html',
  styleUrls: ['./domain-list-control.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DomainListControlComponent),
    multi: true
  }]
})
export class DomainListControlComponent implements ControlValueAccessor {
  @ViewChild('domainInput') domainInput: ElementRef<HTMLInputElement>;

  @Input() disabled = false;
  @Input() inputId: string;

  domains: string[] = [];
  inputValue = '';
  validationMessage = '';

  private readonly domainRegEx = /^(?=.{1,253}$)(?!-)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;
  private onChange: (value: string[]) => void = () => null;
  private onTouched: () => void = () => null;

  writeValue(value: string[]): void {
    this.domains = this.normalizeDomains(value || []);
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  focusInput(): void {
    if (!this.disabled && this.domainInput) {
      this.domainInput.nativeElement.focus();
    }
  }

  inputChanged(value: string): void {
    this.inputValue = value;
    this.validationMessage = '';

    if (/[\s,;]/.test(value || '')) {
      this.addDomains(value);
    }
  }

  inputKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === 'Tab' || event.key === ',' || event.key === ';') {
      if (this.inputValue.trim()) {
        event.preventDefault();
        this.addDomains(this.inputValue);
      }
      return;
    }

    if (event.key === 'Backspace' && !this.inputValue && this.domains.length) {
      this.removeDomain(this.domains[this.domains.length - 1], event);
    }
  }

  inputBlur(): void {
    this.onTouched();

    if (this.inputValue.trim()) {
      this.addDomains(this.inputValue);
    }
  }

  inputPaste(event: ClipboardEvent): void {
    const text = event.clipboardData?.getData('text');

    if (!text || !/[\s,;]/.test(text)) {
      return;
    }

    event.preventDefault();
    this.addDomains(text);
  }

  removeDomain(domain: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.domains = this.domains.filter(item => item !== domain);
    this.emitValue();
  }

  trackDomain(index: number, domain: string): string {
    return domain;
  }

  private addDomains(text: string): void {
    const candidates = (text || '')
      .split(/[\s,;]+/)
      .map(domain => this.normalizeDomain(domain))
      .filter(domain => !!domain);
    const validDomains = candidates.filter(domain => this.domainRegEx.test(domain));
    const invalidDomains = candidates.filter(domain => !this.domainRegEx.test(domain));

    if (validDomains.length) {
      this.domains = this.normalizeDomains([...this.domains, ...validDomains]);
      this.inputValue = '';
      this.emitValue();
    }

    this.validationMessage = invalidDomains.length ? `Invalid domain: ${invalidDomains[0]}` : '';
  }

  private emitValue(): void {
    this.onTouched();
    this.onChange([...this.domains]);
  }

  private normalizeDomains(domains: string[]): string[] {
    return domains
      .map(domain => this.normalizeDomain(domain))
      .filter((domain, index, values) => !!domain && this.domainRegEx.test(domain) && values.indexOf(domain) === index);
  }

  private normalizeDomain(domain: string): string {
    return (domain || '').trim().replace(/^@/, '').toLowerCase();
  }
}
